/** @jsx jsx */

import { Fragment } from 'react';

import { Spinner } from '@zapier/design-system';
import { Border, Colors } from '@zapier/theme';
import { DropdownMenu, Icon } from '@zapier/common-components';
import { css, jsx } from '@emotion/core';

import ServiceIcon from 'app/developer-v3/components/ServiceIcon';
import StatusLabel from 'app/developer-v3/components/StatusLabel';

import { IntegrationType, ItemValue, ItemVersionValue } from './types';
import { buildMenuItems } from './utils';

const Styles = {
  cell: (
    {
      isAction = false,
      isStatus = false,
      shouldIndent = false,
    }: {
      isAction?: boolean;
      isStatus?: boolean;
      shouldIndent?: boolean;
    } = {
      isAction: false,
      isStatus: false,
      shouldIndent: false,
    }
  ) => [
    css`
      line-height: 1.5;
      padding: 15px;
      color: ${Colors.steel};
    `,
    isAction &&
      css`
        width: 16px;
      `,
    isStatus &&
      css`
        white-space: nowrap;
      `,
    shouldIndent &&
      css`
        padding-left: 28px;
      `,
  ],
  logo: css`
    display: inline-block;
    margin-right: 14px;
    vertical-align: middle;
  `,
  menuItem: ({ isDisabled }: { isDisabled: boolean }) => css`
    cursor: ${isDisabled ? 'not-allowed' : 'default'};
    flex: 1;
    opacity: ${isDisabled ? 0.7 : 1};
    padding: 10px;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  root: ({ isDisabled }: { isDisabled?: boolean }) => [
    css`
      cursor: pointer;
      vertical-align: middle;
      :not(:last-of-type) {
        border-bottom: 1px solid ${Border.color.default};
      }
      :hover {
        background-color: ${Colors.grey200};
      }
    `,
    isDisabled &&
      css`
        opacity: 0.6;
        :hover {
        }
      `,
  ],
  status: css`
    align-items: center;
    display: flex;
    gap: 5px;
    white-space: initial;
  `,
};

type Props = {
  integration: IntegrationType;
  isIntegrationAdmin: boolean;
  isDisabled?: boolean;
  isLoadingActions: boolean;
  isShowingVersions: boolean;
  isVersion: boolean;
  onChoose: (value: ItemValue | ItemVersionValue) => void;
  onClickToggle?: () => void;
};

function ItemRow({
  integration,
  isIntegrationAdmin,
  isDisabled,
  isLoadingActions,
  isShowingVersions,
  isVersion,
  onChoose,
  onClickToggle = () => {},
}: Props) {
  const actionItems = buildMenuItems(integration, isIntegrationAdmin);

  if (!isVersion) {
    const versions = [
      ...(integration.versions || []).filter(
        version => version !== integration.version
      ),
    ];

    const versionActionItem = {
      label: isShowingVersions ? 'Hide Versions' : 'Show Versions',
      value: isShowingVersions ? 'hide-versions' : 'show-versions',
      isDisabled: versions.length === 0,
    };
    actionItems.splice(1, 0, versionActionItem);
  }

  return (
    <tr
      css={Styles.root({ isDisabled })}
      onClick={isDisabled ? () => null : () => onChoose('view')}
    >
      <td css={Styles.cell({ shouldIndent: isVersion })}>
        <span css={Styles.logo}>
          <ServiceIcon
            isSymbol={false}
            serviceName={integration.title}
            size={40}
            src={integration.image}
          />
        </span>
        {integration.title}
      </td>
      <td css={Styles.cell()}>{integration.version}</td>
      <td css={Styles.cell({ isStatus: true })}>
        <div css={Styles.status}>
          {isDisabled ? (
            <span>Pending Deletion</span>
          ) : (
            <Fragment>
              <StatusLabel
                color={
                  integration.status.published &&
                  (integration.isPublic || integration.isBeta)
                    ? 'success500'
                    : 'gray'
                }
                label={integration.status.display}
                size="small"
              />
              {integration.isBeta && (
                <StatusLabel color="neutral300" label="Beta" size="small" />
              )}
            </Fragment>
          )}
        </div>
      </td>
      <td aria-label="Actions" css={Styles.cell({ isAction: true })}>
        {!isDisabled && (
          <div
            onClick={event => {
              event.stopPropagation();
              onClickToggle();
            }}
            role="presentation"
          >
            <DropdownMenu
              items={
                isLoadingActions
                  ? [{ label: <Spinner />, value: 'loading' }]
                  : actionItems
              }
              MenuItemComponent={({
                getLabelForItem,
                isConfirming,
                item,
              }: {
                getLabelForItem: (item: any, isConfirming: boolean) => string;
                isConfirming: boolean;
                item: { isDisabled: boolean; label: string; value: any };
              }) => (
                <div css={Styles.menuItem({ isDisabled: item.isDisabled })}>
                  {getLabelForItem(item, isConfirming)}
                </div>
              )}
              onSelect={({ value }: { value: any }) => onChoose(value)}
              showDividers={true}
              ToggleComponent={() => <Icon name="cog-gear" size={18} />}
            />
          </div>
        )}
      </td>
    </tr>
  );
}

export default ItemRow;
